import { UploadedDashboardFile } from '@api/insight-dashboards';
import {
  GridDashboardItem,
  MediaFile,
  ToUploadMediaFile,
  UploadedMediaFile,
} from '@g17eco/types/insight-custom-dashboard';
import { isMediaType } from '@routes/custom-dashboard/utils';
import { checkIsVideo } from '@features/custom-dashboard/utils/dashboard-utils';

export const MAX_SIZE_MB = 50;
export const MAX_SIZE = MAX_SIZE_MB * 1024 * 1024;
export const ACCEPT = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/gif': ['.gif'],
  'video/mp4': ['.mp4'],
};

export const getFileRatio = async (file: Pick<MediaFile, 'type' | 'url'>) => {
  if (checkIsVideo(file)) {
    return 16 / 9;
  }

  const img = new Image();
  img.src = file.url;
  await img.decode();

  return img.width / img.height;
};

/** Type guard: file has been uploaded to document service (has documentId) */
const isUploadedFile = (file: MediaFile): file is UploadedMediaFile => {
  return 'documentId' in file && Boolean(file.documentId);
};

/** Type guard: file pending upload (has originalFile) */
const isToUploadFile = (file: MediaFile): file is ToUploadMediaFile => {
  return 'originalFile' in file;
};

export const getNewFilesToUpload = (newItems: GridDashboardItem[]): { [key: string]: File[] } => {
  return newItems.reduce((newFiles, item) => {
    if (!isMediaType(item)) {
      return newFiles;
    }

    const filesToUpload = (item.files ?? []).reduce((files, file) => {
      if (!isToUploadFile(file) || isUploadedFile(file)) {
        return files;
      }
      return files.concat(file.originalFile);
    }, [] as File[]);

    if (!filesToUpload.length) {
      return newFiles;
    }

    return {
      ...newFiles,
      [item._id]: filesToUpload,
    };
  }, {} as { [key: string]: File[] });
};

type SavedMediaFile =
  | { documentId: string; ratio?: number }
  | { url: string; type: string; name: string; ratio?: number };

export const mapFilesToItems = (uploadedFiles: UploadedDashboardFile[], items: GridDashboardItem[]) => {
  return items.map((item) => {
    if (!isMediaType(item)) {
      return item;
    }

    return {
      ...item,
      files: item.files?.reduce((files, itemFile) => {
        // StaticMediaFile - keep as-is (no documentId)
        if (!isUploadedFile(itemFile) && !isToUploadFile(itemFile)) {
          return [...files, { url: itemFile.url, type: itemFile.type, name: itemFile.name, ratio: itemFile.ratio }];
        }

        // UploadedMediaFile - only keep documentId and ratio
        if (isUploadedFile(itemFile)) {
          return [...files, { documentId: itemFile.documentId, ratio: itemFile.ratio }];
        }

        // ToUploadMediaFile - find the uploaded result and save documentId
        const uploadedFile = uploadedFiles.find((f) => f.name === itemFile.originalFile.name && f.itemId === item._id);
        return uploadedFile ? [...files, { documentId: uploadedFile.documentId, ratio: itemFile.ratio }] : files;
      }, [] as SavedMediaFile[]),
    };
  });
};

const getDocumentIdsFromItems = (items: GridDashboardItem[]) => {
  return items.reduce((ids, item) => {
    if (isMediaType(item) && item.files) {
      item.files.forEach((file) => {
        // Only UploadedMediaFile has documentId
        if (isUploadedFile(file)) {
          ids.push(file.documentId);
        }
      });
    }

    return ids;
  }, [] as string[]);
};

export const getDeletedDocumentIds = (oldItems: GridDashboardItem[], newItems: GridDashboardItem[]) => {
  const oldDocumentIds = getDocumentIdsFromItems(oldItems);
  const newDocumentIds = getDocumentIdsFromItems(newItems);

  return oldDocumentIds.filter((id) => !newDocumentIds.includes(id));
};

export const cloneFile = (file: File, customName: string) => {
  return new File([file], customName, { type: file.type, lastModified: file.lastModified });
}
